import { createContext, useContext, useMemo } from 'react';
import { useTokenEstimates, useJobEstimates } from './hooks/useMachineIntervals.js';
import { EstimateCell, fmtEta } from '../../components/machines/EstimateCell.js';

// Estimates for a whole table in ONE request, looked up per row.
//
// Table column definitions in this app are module-level constants, so threading
// estimates down as props would mean converting every one of them into a factory.
// Context keeps each integration to a provider around the table and a cell that
// reads from it.
const EstimateContext = createContext({ byKey: {}, isLoading: false });

/**
 * @param rows  the table's rows
 * @param map   row → { key, tokenId, machineId, phase, productLabel, color,
 *                      plannedM, producedM, started } or null to skip the row
 */
export const EstimateProvider = ({ rows = [], map, operator = null, children }) => {
  const mapped = useMemo(() => {
    if (typeof map !== 'function') return [];
    return rows
      .map(map)
      .filter(Boolean)
      // nothing left to do costs nothing — do not send it, do not show a number
      .filter(t => Number(t.plannedM) > Number(t.producedM || 0));
  }, [rows, map]);

  const tokens = useMemo(() => mapped.map(({ key, ...t }) => t), [mapped]);
  const { data, isLoading } = useTokenEstimates(tokens, operator);

  const value = useMemo(() => {
    const perToken = data?.data?.perToken || [];
    const byKey = {};
    // perToken comes back in the order it was sent
    mapped.forEach((m, i) => {
      if (perToken[i]) byKey[m.key] = perToken[i];
    });
    return { byKey, isLoading, totals: data?.data?.totals || null };
  }, [data, mapped, isLoading]);

  return <EstimateContext.Provider value={value}>{children}</EstimateContext.Provider>;
};

export const useEstimates = () => useContext(EstimateContext);

/** Drop-in table cell: reads the batched estimate for one row. */
export const RowEstimate = ({ id, compact = false }) => {
  const { byKey } = useEstimates();
  return <EstimateCell est={byKey[id]} compact={compact} />;
};

// ── job-level ──────────────────────────────────────────────────────────────

const JobEstimateContext = createContext({ byJob: {}, isLoading: false });

/**
 * Estimates for tables whose rows are jobs or allocated products rather than
 * drums. The backend expands each job into its unfinished drums and costs each
 * on the machine that will run it, so the number is the job's remaining work —
 * not just whatever its current phase has left.
 */
export const JobEstimateProvider = ({ jobIds = [], operator = null, children }) => {
  const { data, isLoading } = useJobEstimates(jobIds, operator);
  const value = useMemo(
    () => ({ byJob: data?.data?.byJob || {}, isLoading }),
    [data, isLoading]
  );
  return <JobEstimateContext.Provider value={value}>{children}</JobEstimateContext.Provider>;
};

export const useJobEstimateMap = () => useContext(JobEstimateContext);

/** Drop-in table cell for a job row. */
export const JobEstimate = ({ jobId }) => {
  const { byJob } = useJobEstimateMap();
  const j = byJob[jobId];

  if (!j || !j.totalSec) return <span className="text-700">—</span>;

  return (
    <div className="d-flex flex-column">
      <span className="fw-semibold">~{fmtEta(j.totalSec)}</span>
      <span className="fs-10 text-700 fw-normal" title="Remaining production and setup across all unfinished drums of this job">
        {j.drums} {j.drums === 1 ? 'drum' : 'drums'} · {Math.round(j.remainingM).toLocaleString()} m
      </span>
    </div>
  );
};

export default EstimateProvider;
